/**
 * A small, dependency-free schema validation library shared by the API and the
 * web forms. Schemas coerce common form input (e.g. "12" → 12, "" → null for
 * optional fields) and report errors keyed by dotted field path.
 */
import { isISODate } from "./dates.ts";
import { parsePounds } from "./money.ts";

export type FieldErrors = Record<string, string>;
export type ParseResult<T> = { ok: true; value: T } | { ok: false; errors: FieldErrors };

const INVALID: unique symbol = Symbol("invalid");
type Out<T> = T | typeof INVALID;

class Ctx {
  errors: FieldErrors = {};
  add(path: string, message: string): typeof INVALID {
    const key = path || "_";
    if (!(key in this.errors)) this.errors[key] = message;
    return INVALID;
  }
}

function isEmpty(input: unknown): boolean {
  return input === undefined || input === null || (typeof input === "string" && input.trim() === "");
}

export abstract class Schema<T> {
  /** @internal */
  abstract run(input: unknown, ctx: Ctx, path: string): Out<T>;

  parse(input: unknown): ParseResult<T> {
    const ctx = new Ctx();
    const value = this.run(input, ctx, "");
    if (value === INVALID || Object.keys(ctx.errors).length > 0) return { ok: false, errors: ctx.errors };
    return { ok: true, value };
  }

  /** Missing / empty input becomes null. */
  optional(): Schema<T | null> {
    return new OptionalSchema(this);
  }

  /** Missing / empty input becomes the given default. */
  default(value: T): Schema<T> {
    return new DefaultSchema(this, value);
  }

  refine(check: (value: T) => boolean, message: string): Schema<T> {
    return new RefineSchema(this, check, message);
  }

  transform<U>(fn: (value: T) => U): Schema<U> {
    return new TransformSchema(this, fn);
  }
}

export type Infer<S> = S extends Schema<infer T> ? T : never;

class OptionalSchema<T> extends Schema<T | null> {
  constructor(private inner: Schema<T>) {
    super();
  }
  run(input: unknown, ctx: Ctx, path: string): Out<T | null> {
    return isEmpty(input) ? null : this.inner.run(input, ctx, path);
  }
}

class DefaultSchema<T> extends Schema<T> {
  constructor(
    private inner: Schema<T>,
    private fallback: T,
  ) {
    super();
  }
  run(input: unknown, ctx: Ctx, path: string): Out<T> {
    return isEmpty(input) ? this.fallback : this.inner.run(input, ctx, path);
  }
}

class RefineSchema<T> extends Schema<T> {
  constructor(
    private inner: Schema<T>,
    private check: (value: T) => boolean,
    private message: string,
  ) {
    super();
  }
  run(input: unknown, ctx: Ctx, path: string): Out<T> {
    const value = this.inner.run(input, ctx, path);
    if (value === INVALID) return INVALID;
    return this.check(value) ? value : ctx.add(path, this.message);
  }
}

class TransformSchema<T, U> extends Schema<U> {
  constructor(
    private inner: Schema<T>,
    private fn: (value: T) => U,
  ) {
    super();
  }
  run(input: unknown, ctx: Ctx, path: string): Out<U> {
    const value = this.inner.run(input, ctx, path);
    return value === INVALID ? INVALID : this.fn(value);
  }
}

class FnSchema<T> extends Schema<T> {
  constructor(private fn: (input: unknown, add: (message: string) => typeof INVALID) => Out<T>) {
    super();
  }
  run(input: unknown, ctx: Ctx, path: string): Out<T> {
    return this.fn(input, (message) => ctx.add(path, message));
  }
}

function required(input: unknown, add: (m: string) => typeof INVALID): boolean {
  if (isEmpty(input)) {
    add("This field is required");
    return false;
  }
  return true;
}

interface StringOpts {
  min?: number;
  max?: number;
  pattern?: RegExp;
  patternMessage?: string;
  trim?: boolean;
}

function string(opts: StringOpts = {}): Schema<string> {
  const { min = 0, max = 10_000, trim = true } = opts;
  return new FnSchema((input, add) => {
    if (!required(input, add)) return INVALID;
    if (typeof input !== "string" && typeof input !== "number") return add("Must be text");
    const value = trim ? String(input).trim() : String(input);
    if (value.length < min) return add(`Must be at least ${min} characters`);
    if (value.length > max) return add(`Must be at most ${max} characters`);
    if (opts.pattern && !opts.pattern.test(value)) return add(opts.patternMessage ?? "Invalid format");
    return value;
  });
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** UK postcode (incl. GIR 0AA), case-insensitive, optional space. */
export const UK_POSTCODE_RE = /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;

function int(opts: { min?: number; max?: number } = {}): Schema<number> {
  return new FnSchema((input, add) => {
    if (!required(input, add)) return INVALID;
    const n = typeof input === "string" ? Number(input.trim()) : input;
    if (typeof n !== "number" || !Number.isInteger(n)) return add("Must be a whole number");
    if (opts.min !== undefined && n < opts.min) return add(`Must be at least ${opts.min}`);
    if (opts.max !== undefined && n > opts.max) return add(`Must be at most ${opts.max}`);
    return n;
  });
}

/** Integer pence. Accepts an integer, or a pounds string like "1,234.50" when `fromPounds` is set. */
function pence(opts: { min?: number; max?: number; fromPounds?: boolean } = {}): Schema<number> {
  const { min = -1_000_000_000_00, max = 1_000_000_000_00 } = opts;
  return new FnSchema((input, add) => {
    if (!required(input, add)) return INVALID;
    let value: number | null;
    if (typeof input === "number") value = Number.isInteger(input) ? input : null;
    else if (typeof input === "string")
      value = opts.fromPounds ? parsePounds(input) : /^-?\d+$/.test(input.trim()) ? Number(input) : null;
    else value = null;
    if (value === null) return add("Must be a valid amount");
    if (value < min) return add(min === 0 ? "Must not be negative" : "Amount is too small");
    if (value > max) return add("Amount is too large");
    return value;
  });
}

function boolean(): Schema<boolean> {
  return new FnSchema((input, add) => {
    if (typeof input === "boolean") return input;
    if (input === "true" || input === "on" || input === "1") return true;
    if (input === "false" || input === "0" || input === "" || input === undefined || input === null)
      return false;
    return add("Must be true or false");
  });
}

function date(): Schema<string> {
  return new FnSchema((input, add) => {
    if (!required(input, add)) return INVALID;
    if (!isISODate(input)) return add("Must be a valid date (YYYY-MM-DD)");
    return input;
  });
}

function enumOf<const T extends readonly string[]>(values: T): Schema<T[number]> {
  return new FnSchema((input, add) => {
    if (!required(input, add)) return INVALID;
    if (typeof input !== "string" || !values.includes(input)) return add("Please choose a valid option");
    return input as T[number];
  });
}

/** Percentage with up to 2 decimals ("33.33", 50) → basis points (3333, 5000). */
function percent(opts: { min?: number; max?: number } = {}): Schema<number> {
  const { min = 1, max = 10000 } = opts;
  return new FnSchema((input, add) => {
    if (!required(input, add)) return INVALID;
    let bp: number;
    // Like pence(): text is what a person typed ("4.79"); a whole number is already basis points,
    // as the form sends it after validating in the browser.
    if (typeof input === "number") {
      if (!Number.isInteger(input)) return add("Must be a percentage with up to 2 decimal places");
      bp = input;
    } else {
      const text = String(input).trim().replace(/%$/, "");
      if (!/^\d{1,3}(\.\d{1,2})?$/.test(text)) return add("Must be a percentage with up to 2 decimal places");
      const [whole, frac = ""] = text.split(".");
      bp = Number(whole) * 100 + Number(frac.padEnd(2, "0"));
    }
    if (bp < min || bp > max) return add(`Must be between ${min / 100}% and ${max / 100}%`);
    return bp;
  });
}

const DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** ISO date-time (or datetime-local value) → ISO 8601 UTC string. */
function datetime(): Schema<string> {
  return new FnSchema((input, add) => {
    if (!required(input, add)) return INVALID;
    if (typeof input !== "string" || !DATETIME_RE.test(input) || Number.isNaN(Date.parse(input)))
      return add("Must be a valid date and time");
    return new Date(input).toISOString();
  });
}

/** Treats an object as absent (null) unless `key` has a value — for optional form sections. */
function optionalUnless<T>(schema: Schema<T>, key: string): Schema<T | null> {
  return new (class extends Schema<T | null> {
    run(input: unknown, ctx: Ctx, path: string): Out<T | null> {
      if (isEmpty(input)) return null;
      if (typeof input === "object" && isEmpty((input as Record<string, unknown>)[key])) return null;
      return schema.run(input, ctx, path);
    }
  })();
}

type Shape = Record<string, Schema<unknown>>;
type ObjectOut<S extends Shape> = { [K in keyof S]: Infer<S[K]> };

class ObjectSchema<S extends Shape> extends Schema<ObjectOut<S>> {
  constructor(readonly shape: S) {
    super();
  }
  run(input: unknown, ctx: Ctx, path: string): Out<ObjectOut<S>> {
    if (typeof input !== "object" || input === null || Array.isArray(input)) {
      return ctx.add(path, "Must be an object");
    }
    const source = input as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    let invalid = false;
    for (const [key, schema] of Object.entries(this.shape)) {
      const value = schema.run(source[key], ctx, path ? `${path}.${key}` : key);
      if (value === INVALID) invalid = true;
      else out[key] = value;
    }
    return invalid ? INVALID : (out as ObjectOut<S>);
  }
  /** A copy where every field is optional (null when missing). */
  partial(): ObjectSchema<{ [K in keyof S]: Schema<Infer<S[K]> | null> }> {
    const shape: Record<string, Schema<unknown>> = {};
    for (const [k, s] of Object.entries(this.shape)) shape[k] = s.optional();
    return new ObjectSchema(shape) as never;
  }
  extend<E extends Shape>(extra: E): ObjectSchema<S & E> {
    return new ObjectSchema({ ...this.shape, ...extra });
  }
}

function array<T>(item: Schema<T>, opts: { min?: number; max?: number } = {}): Schema<T[]> {
  return new (class extends Schema<T[]> {
    run(input: unknown, ctx: Ctx, path: string): Out<T[]> {
      const list = input === undefined || input === null ? [] : input;
      if (!Array.isArray(list)) return ctx.add(path, "Must be a list");
      if (opts.min !== undefined && list.length < opts.min)
        return ctx.add(path, opts.min === 1 ? "Add at least one item" : `Add at least ${opts.min} items`);
      if (opts.max !== undefined && list.length > opts.max) return ctx.add(path, `At most ${opts.max} items`);
      const out: T[] = [];
      let invalid = false;
      list.forEach((el, i) => {
        const v = item.run(el, ctx, `${path}.${i}`);
        if (v === INVALID) invalid = true;
        else out.push(v);
      });
      return invalid ? INVALID : out;
    }
  })();
}

export const v = {
  string,
  email: () =>
    string({ max: 254, pattern: EMAIL_RE, patternMessage: "Must be a valid email address" }).transform((s) =>
      s.toLowerCase(),
    ),
  uuid: () => string({ pattern: UUID_RE, patternMessage: "Invalid identifier" }),
  postcode: () =>
    string({ pattern: UK_POSTCODE_RE, patternMessage: "Must be a valid UK postcode" }).transform(
      formatPostcode,
    ),
  int,
  pence,
  percent,
  datetime,
  boolean,
  date,
  enum: enumOf,
  object: <S extends Shape>(shape: S) => new ObjectSchema(shape),
  array,
  optionalUnless,
  custom: <T>(fn: (input: unknown, add: (message: string) => typeof INVALID) => Out<T>) => new FnSchema(fn),
};

export type { ObjectSchema };

/** Normalises a UK postcode to upper case with a single space before the inward code. */
export function formatPostcode(input: string): string {
  const compact = input.replace(/\s+/g, "").toUpperCase();
  return `${compact.slice(0, -3)} ${compact.slice(-3)}`;
}

/** Validate that `to` is not before `from` (both optional). */
export function dateOrder<T extends Record<string, unknown>>(
  fromKey: keyof T & string,
  toKey: keyof T & string,
  message = "End date must be on or after the start date",
): (value: T) => FieldErrors | null {
  return (value) => {
    const from = value[fromKey];
    const to = value[toKey];
    if (typeof from === "string" && typeof to === "string" && to < from) return { [toKey]: message };
    return null;
  };
}

/** Parses with a schema, then runs cross-field checks (each returns field errors or null). */
export function validate<T>(
  schema: Schema<T>,
  input: unknown,
  ...checks: Array<(value: T) => FieldErrors | null>
): ParseResult<T> {
  const result = schema.parse(input);
  if (!result.ok) return result;
  const errors: FieldErrors = {};
  for (const check of checks) Object.assign(errors, check(result.value) ?? {});
  return Object.keys(errors).length ? { ok: false, errors } : result;
}
